import axiosInstance from 'axiosInstance';

const SUMMARY_CACHE_MS = 15000;
const SUMMARY_EVENT_NAME = 'gestodo:notifications-summary-updated';
const INCOMING_EVENT_NAME = 'gestodo:notifications-incoming';

let notificationsSummaryPromise = null;
let notificationsSummaryCache = null;
let notificationsSummaryFetchedAt = 0;

const publishNotificationsSummary = summary => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new window.CustomEvent(SUMMARY_EVENT_NAME, {
      detail: summary || null
    })
  );
};

export const publishIncomingNotifications = notifications => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = Array.isArray(notifications) ? notifications : [];
  window.dispatchEvent(
    new window.CustomEvent(INCOMING_EVENT_NAME, {
      detail: normalized
    })
  );
};

export const invalidateNotificationsSummaryCache = () => {
  notificationsSummaryPromise = null;
  notificationsSummaryCache = null;
  notificationsSummaryFetchedAt = 0;
};

export const getCachedNotificationsSummary = () => notificationsSummaryCache;

export const subscribeNotificationsSummary = listener => {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }

  const handleUpdate = event => {
    listener(event?.detail || null);
  };

  window.addEventListener(SUMMARY_EVENT_NAME, handleUpdate);
  return () => {
    window.removeEventListener(SUMMARY_EVENT_NAME, handleUpdate);
  };
};

export const subscribeIncomingNotifications = listener => {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }

  const handleUpdate = event => {
    listener(Array.isArray(event?.detail) ? event.detail : []);
  };

  window.addEventListener(INCOMING_EVENT_NAME, handleUpdate);
  return () => {
    window.removeEventListener(INCOMING_EVENT_NAME, handleUpdate);
  };
};

export const fetchNotificationsSummary = async options => {
  const force = Boolean(options?.force);
  const now = Date.now();

  if (
    !force &&
    notificationsSummaryCache &&
    now - notificationsSummaryFetchedAt < SUMMARY_CACHE_MS
  ) {
    return notificationsSummaryCache;
  }

  if (!force && notificationsSummaryPromise) {
    return notificationsSummaryPromise;
  }

  notificationsSummaryPromise = axiosInstance.get('notifications/summary')
    .then(response => {
      notificationsSummaryCache = response.data;
      notificationsSummaryFetchedAt = Date.now();
      publishNotificationsSummary(notificationsSummaryCache);
      return notificationsSummaryCache;
    })
    .finally(() => {
      notificationsSummaryPromise = null;
    });

  return notificationsSummaryPromise;
};

export const fetchNotificationsFeed = async (category, options = {}) => {
  const params = {};
  if (category) params.category = category;
  if (options.page != null) params.page = options.page;
  if (options.pageSize != null) params.page_size = options.pageSize;

  const response = await axiosInstance.get('notifications/feed', { params });
  return response.data;
};

export const markNotificationsRead = async payload => {
  const response = await axiosInstance.post('notifications/mark-read', payload || {});
  invalidateNotificationsSummaryCache();
  return response.data;
};

export const syncNotifications = async payload => {
  const response = await axiosInstance.post('notifications/sync', payload || {});
  invalidateNotificationsSummaryCache();
  return response.data;
};

export const fetchPushPreferences = async () => {
  const response = await axiosInstance.get('notifications/push/preferences');
  return response.data;
};

export const updatePushPreferences = async payload => {
  const response = await axiosInstance.post('notifications/push/preferences/update', payload || {});
  return response.data;
};
